import type { OfflineDatabaseSqlStatement } from './offlineDatabase.statements';
import {
  addDefinedSqlUpdate,
  addSqlUpdate,
  buildSqlUpdate,
  createSqlUpdateBuilder,
} from './offlineDatabase.sqlUpdates';

type TimestampFactory = () => string;

const isoNow = (): string => new Date().toISOString();

export type OfflineEntitySyncStatus = 'pending' | 'syncing' | 'synced' | 'failed';
export type IdMappingEntityType = 'review' | 'response' | 'room' | 'item';

export interface OfflineRoomInsertInput {
  environmentKey: string;
  tempId: string;
  propertyId: number;
  name: string;
  displayOrder: number;
}

export const buildOfflineRoomInsertStatement = (
  room: OfflineRoomInsertInput,
  getTimestamp: TimestampFactory = isoNow
): OfflineDatabaseSqlStatement => ({
  sql: `INSERT INTO offline_rooms (environment_key, temp_id, property_id, name, display_order, created_at)
           VALUES (?, ?, ?, ?, ?, ?)`,
  params: [
    room.environmentKey,
    room.tempId,
    room.propertyId,
    room.name,
    room.displayOrder,
    getTimestamp(),
  ],
});

export const buildOfflineRoomsSelectStatement = (
  propertyId?: number,
  environmentKey?: string
): OfflineDatabaseSqlStatement => {
  if (propertyId === undefined) {
    return {
      sql: `SELECT * FROM offline_rooms${environmentKey ? ' WHERE environment_key = ?' : ''}`,
      params: environmentKey ? [environmentKey] : [],
    };
  }

  return {
    sql: `SELECT * FROM offline_rooms WHERE property_id = ?${environmentKey ? ' AND environment_key = ?' : ''}`,
    params: environmentKey ? [propertyId, environmentKey] : [propertyId],
  };
};

export const buildOfflineRoomSyncStatusUpdateStatement = (
  tempId: string,
  syncStatus: OfflineEntitySyncStatus,
  syncError?: string,
  realId?: number,
  environmentKey?: string,
  getTimestamp: TimestampFactory = isoNow
): OfflineDatabaseSqlStatement => {
  const updateBuilder = createSqlUpdateBuilder();

  addSqlUpdate(updateBuilder, 'sync_status', syncStatus);
  addDefinedSqlUpdate(updateBuilder, 'sync_error', syncError);
  if (syncStatus === 'synced') {
    addSqlUpdate(updateBuilder, 'synced_at', getTimestamp());
  }

  addDefinedSqlUpdate(updateBuilder, 'id', realId);

  return buildSqlUpdate(
    'offline_rooms',
    updateBuilder,
    `temp_id = ?${environmentKey ? ' AND environment_key = ?' : ''}`,
    environmentKey ? [tempId, environmentKey] : [tempId]
  )!;
};

export const buildPendingOfflineRoomsSelectStatement = (): OfflineDatabaseSqlStatement => ({
  sql: 'SELECT * FROM offline_rooms WHERE sync_status IN (?, ?, ?) ORDER BY created_at',
  params: ['pending', 'syncing', 'failed'],
});

export const buildPendingOfflineRoomsForEnvironmentSelectStatement = (
  environmentKey: string
): OfflineDatabaseSqlStatement => ({
  sql: 'SELECT * FROM offline_rooms WHERE sync_status IN (?, ?, ?) AND environment_key = ? ORDER BY created_at',
  params: ['pending', 'syncing', 'failed', environmentKey],
});

export const buildIdMappingInsertStatement = (
  tempId: string,
  realId: number,
  entityType: IdMappingEntityType,
  environmentKey: string,
  getTimestamp: TimestampFactory = isoNow
): OfflineDatabaseSqlStatement => {
  const now = getTimestamp();

  return {
    sql: `INSERT OR REPLACE INTO id_mappings (temp_id, environment_key, real_id, entity_type, synced_at, created_at)
           VALUES (?, ?, ?, ?, ?, ?)`,
    params: [tempId, environmentKey, realId, entityType, now, now],
  };
};

export const buildRealIdByTempIdSelectStatement = (
  tempId: string,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT real_id FROM id_mappings WHERE temp_id = ?${environmentKey ? ' AND environment_key = ?' : ''}`,
  params: environmentKey ? [tempId, environmentKey] : [tempId],
});

export const buildTempIdByRealIdSelectStatement = (
  realId: number,
  entityType: IdMappingEntityType,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `SELECT temp_id FROM id_mappings WHERE real_id = ? AND entity_type = ?${environmentKey ? ' AND environment_key = ?' : ''}`,
  params: environmentKey ? [realId, entityType, environmentKey] : [realId, entityType],
});

export const buildAllIdMappingsSelectStatement = (environmentKey?: string): OfflineDatabaseSqlStatement => ({
  sql: `SELECT temp_id, real_id, entity_type, synced_at FROM id_mappings${environmentKey ? ' WHERE environment_key = ?' : ''}`,
  params: environmentKey ? [environmentKey] : [],
});

export const buildResponseRoomIdMappingUpdateStatement = (
  oldRoomId: number,
  newRoomId: number,
  environmentKey?: string
): OfflineDatabaseSqlStatement => ({
  sql: `UPDATE responses SET room_id = ? WHERE room_id = ?${environmentKey ? ' AND environment_key = ?' : ''}`,
  params: environmentKey ? [newRoomId, oldRoomId, environmentKey] : [newRoomId, oldRoomId],
});
